import React, { useState } from 'react';
import cx from 'classnames';
import { filter, find, isEqual, without } from 'lodash';
import { useQueryClient } from 'react-query';
import tinycolor from 'tinycolor2';

import { useModalContext } from '@magoosh/context/modal_context';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { pluralize } from 'utilities/pluralize';

import StatisticData from '@test_prep/app/pages/analytics/components/statistic_data';
import predeterminedColors from '@test_prep/app/pages/analytics/predetermined_colors';

import EstimatedScoreLineChart from './estimated_score_line_chart';
import loadingData from './loading_data';
import SeriesCard from './series_card';
import TargetScoresModal from '../target_scores_modal';
import { Series } from './types';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props {
  examName: string;
  filters: any;
}

const EstimatedScore: React.FC<Props> = (props) => {
  const [selectedSeries, setSelectedSeries] = useState(null);

  const handleDataLoaded = (data, previousData) => {
    const newSeriesNames = data.map((series) => series.name);
    const oldSeriesNames = (previousData || []).map((series) => series.name);

    // If the set of series have changed (due to filters changing), set the first one with a score
    // as active.
    if (!isEqual(newSeriesNames, oldSeriesNames)) {
      setSelectedSeries(find(data, (series) => !!series.score));
    } else if (selectedSeries) {
      const newSelectedSeries = find(data, (series) => series.name === selectedSeries.name);
      setSelectedSeries(newSelectedSeries);
    }
  };

  const overallLoadingData = find(loadingData(props.examName), (series) => series.name === 'Overall');
  const seriesLoadingData = filter(loadingData(props.examName), (series) => series.name !== 'Overall');
  const loadingPlaceholder = (
    <div className="d-flex flex-column flex-lg-row gap-m">
      <div className="flex-1">
        <div className="d-flex flex-column gap-m">
          <h3 className="u-margin-A-n">Estimated Score</h3>

          <div className="d-flex flex-column gap-m gap-lg-s">
            <div className="d-flex flex-column flex-lg-row gap-s">
              <SeriesCard
                active
                colorPalette={{ dark: colors.gray85, light: '#ffffff' }}
                name="Overall"
                onSelected={() => {}}
                score={{}}
                scoreRange={overallLoadingData.scoreRange}
                values={[]}
              />

              <div
                className="d-flex flex-column gap-xxs hidden-md hidden-sm hidden-xs"
                style={{ padding: 15, paddingLeft: 46 }}
              >
                <div className="text-bold" style={{ color: colors.gray85 }}>
                  Percentile
                </div>

                <h2 className="text-bold u-margin-A-n" style={{ color: colors.gray85 }}>
                  -
                </h2>
              </div>
            </div>
          </div>

          <div className="d-flex flex-column flex-lg-row flex-wrap gap-m gap-lg-s">
            {
              seriesLoadingData.map((seriesData) => (
                <SeriesCard
                  key={seriesData.name}
                  colorPalette={{ dark: colors.grayLighter, light: '#ffffff' }}
                  name={seriesData.name}
                  onSelected={() => {}}
                  score={{}}
                  scoreRange={seriesData.scoreRange}
                  values={[]}
                />
              ))
            }
          </div>
        </div>
      </div>

      <div className="flex-1 hidden-sm hidden-xs">
        <EstimatedScoreLineChart
          colorPalette={{ dark: colors.gray85, light: colors.grayLighter }}
          series={{
            name: 'Overall',
            scoreRange: overallLoadingData.scoreRange,
            values: []
          }}
        />
      </div>
    </div>
  );

  if (!props.filters) {
    return loadingPlaceholder;
  } else {
    return (
      <StatisticData
        filters={props.filters}
        name="estimated_score"
        onLoaded={handleDataLoaded}
      >
        <StatisticData.Loading>
          {loadingPlaceholder}
        </StatisticData.Loading>

        {/* @ts-ignore */}
        <EstimatedScoreContent
          examName={props.examName}
          onChangeSelectedSeries={(series) => setSelectedSeries(series)}
          selectedSeries={selectedSeries}
        />
      </StatisticData>
    );
  }
};

interface EstimateScoreContentProps {
  data: Series[];
  examName: string;
  onChangeSelectedSeries: (series: Series) => void;
  selectedSeries: Series;
}

const EstimatedScoreContent: React.FC<EstimateScoreContentProps> = (props) => {
  const { setModal } = useModalContext();
  const queryClient = useQueryClient();

  const overallSeries = find(props.data, (series) => series.name === 'Overall');
  const selectedSeries = props.selectedSeries || props.data[0];

  const colorPaletteForLineChart = () => {
    if (!selectedSeries.score) {
      return { dark: colors.gray85, light: colors.grayLighter };
    }

    let predeterminedColorForLineChart;

    if (selectedSeries.name == 'Overall') {
      predeterminedColorForLineChart = colors.brandSecondary;
    } else {
      predeterminedColorForLineChart = predeterminedColors(selectedSeries.name, props.examName);
    }

    return {
      dark: predeterminedColorForLineChart,
      light: tinycolor(predeterminedColorForLineChart).setAlpha(0.2).toRgbString()
    };
  };

  const presentTargetScoresModal = () => {
    setModal({
      header: 'Target Scores',
      body: (
        <TargetScoresModal
          onClose={() => setModal(null)}
          onUpdate={() => {
            queryClient.resetQueries({
              predicate: (query) => query.queryKey[0] === 'statistics'
            })
          }}
        />
      )
    });
  };

  return (
    <div className="d-flex flex-row gap-l">
      <div className="flex-1">
        <div className="d-flex flex-column gap-m">
          <div className="align-items-center d-flex flex-row gap-m">
            <h3 className="u-margin-A-n">Estimated Score</h3>

            <div className="align-items-center d-flex flex-row gap-xs">
              <FontAwesomeIcon className="text-gray-light" icon="bullseye" />
              <a className="small text-gray-light" onClick={presentTargetScoresModal}>
                {props.data.some((series) => !series.target) ? 'Set targets' : 'Edit targets'}
              </a>
            </div>
          </div>

          <div className="d-flex flex-column gap-m gap-lg-s">
            {
              overallSeries && (
                overallSeries.score ? (
                  <div className="d-flex flex-column flex-lg-row gap-s">
                    <SeriesCard
                      {...overallSeries}
                      active={overallSeries.name === selectedSeries.name}
                      colorPalette={{ dark: colors.brandSecondary, light: '#ffffff' }}
                      onSelected={() => props.onChangeSelectedSeries(overallSeries)}
                    />

                    <div
                      className="d-flex flex-column gap-xxs hidden-md hidden-sm hidden-xs"
                      style={{ padding: 15, paddingLeft: 46 }}
                    >
                      <div className={cx(style.textDivider, 'small text-bold text-secondary')}>
                        Percentiles
                      </div>

                      <div className="align-items-center d-flex gap-m">
                        {
                          without(props.data, overallSeries).map((series, idx) => (
                            <React.Fragment key={series.name}>
                              {idx !== 0 && <div style={{ backgroundColor: colors.grayLighter, height: '80%', width: 1 }} />}

                              <div className="d-flex flex-column gap-xs">
                                <div className="small text-bold text-secondary" style={{ lineHeight: 1.25 }}>
                                  {series.name}
                                </div>

                                <h3 className="text-bold text-secondary u-margin-A-n">
                                  {series.scorePercentile}%
                                </h3>
                              </div>
                            </React.Fragment>
                          ))
                        }
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={cx(style.noOverallScoreMessage, 'align-items-center d-flex flex-row gap-m')}>
                    <div className="hidden-sm hidden-xs">
                      <svg width="80" height="81" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M27.464 10.259a4.179 4.179 0 1 0-.006-8.358 4.179 4.179 0 0 0 .006 8.358Zm0-5.32c.632 0 1.136.512 1.136 1.144 0 .632-.512 1.136-1.136 1.136a1.149 1.149 0 0 1-1.144-1.144c0-.632.512-1.136 1.144-1.136ZM24.952 54.395a4.18 4.18 0 0 0-4.176 4.184 4.18 4.18 0 0 0 4.176 4.184 4.188 4.188 0 0 0 4.184-4.184 4.188 4.188 0 0 0-4.184-4.184Zm0 5.32a1.137 1.137 0 0 1-1.136-1.144c0-.632.512-1.144 1.136-1.144a1.144 1.144 0 1 1 0 2.288ZM39.464 10.043a1.904 1.904 0 1 0 0-3.808 1.904 1.904 0 0 0 0 3.808ZM58.12 76.707a1.904 1.904 0 1 0 0-3.808 1.904 1.904 0 0 0 0 3.808ZM75.136 70.075h-2.28v-2.28a1.52 1.52 0 1 0-3.04 0v2.28h-2.28a1.52 1.52 0 1 0 0 3.04h2.28v2.28a1.52 1.52 0 1 0 3.04 0v-2.28h2.28a1.52 1.52 0 1 0 0-3.04ZM7.688 10.859v2.28a1.52 1.52 0 1 0 3.04 0v-2.28h2.28a1.52 1.52 0 1 0 0-3.04h-2.28v-2.28a1.52 1.52 0 1 0-3.04 0v2.28h-2.28a1.52 1.52 0 1 0 0 3.04h2.28Z" fill="#4E2079"/><path d="M75.352 50.779 63.176 7.163a4.588 4.588 0 0 0-2.808-3.072 4.568 4.568 0 0 0-4.136.44L44.32 12.259a5.894 5.894 0 0 1-3.384.944l-16.064-.488a8.923 8.923 0 0 0-5.136 1.432L7.352 22.179a6.426 6.426 0 0 0-2.704 7.16l12.184 43.616a4.588 4.588 0 0 0 2.808 3.072 4.552 4.552 0 0 0 4.136-.44l11.904-7.72a5.955 5.955 0 0 1 3.384-.944l16.064.488c1.824.064 3.6-.44 5.136-1.432l12.384-8.032a6.442 6.442 0 0 0 2.704-7.168Zm-4.36 4.608-12.384 8.032a5.877 5.877 0 0 1-3.384.944l-16.064-.488a8.863 8.863 0 0 0-5.136 1.432l-11.904 7.72a1.568 1.568 0 0 1-1.408.152 1.526 1.526 0 0 1-.952-1.048L7.576 28.523a3.4 3.4 0 0 1 1.432-3.792l12.384-8.032a5.853 5.853 0 0 1 3.2-.952h.184l16.064.488c1.824.072 3.6-.44 5.128-1.432l11.904-7.72a1.568 1.568 0 0 1 1.408-.152c.472.176.824.56.952 1.048l12.184 43.616a3.38 3.38 0 0 1-1.424 3.792Z" fill="#4E2079"/><path d="M24.2 25.883a1.52 1.52 0 1 0 2.928-.816L25.8 20.299a1.52 1.52 0 0 0-2.928.816l1.328 4.768ZM29.456 33.395a1.52 1.52 0 0 0-2.928.816l7.312 26.168a1.52 1.52 0 1 0 2.928-.816l-7.312-26.168ZM56.104 55.323a1.52 1.52 0 0 0-2.928.816l1.192 4.272a1.52 1.52 0 1 0 2.928-.816l-1.192-4.272ZM50.848 47.819a1.52 1.52 0 1 0 2.928-.816l-7.448-26.664a1.52 1.52 0 0 0-2.928.816l7.448 26.664ZM51.488 22.579l2.44 1.376-1.376 2.44a1.522 1.522 0 0 0 .576 2.072c.232.136.496.2.744.2.528 0 1.048-.28 1.328-.776l1.376-2.44 2.44 1.376c.232.136.496.2.744.2a1.522 1.522 0 0 0 .752-2.848l-2.44-1.376 1.376-2.44a1.52 1.52 0 1 0-2.648-1.496l-1.376 2.44-2.44-1.376a1.52 1.52 0 1 0-1.496 2.648ZM22.112 48.403a1.52 1.52 0 0 0-1.056 1.872l.416 1.488a1.52 1.52 0 1 0 2.928-.816l-.416-1.488a1.52 1.52 0 0 0-1.872-1.056ZM31.904 31.09c.848.249 1.64.633 2.368 1.137a1.512 1.512 0 0 0 2.112-.384c.48-.688.304-1.64-.384-2.112a11.31 11.31 0 0 0-3.24-1.552 1.52 1.52 0 0 0-1.888 1.024 1.53 1.53 0 0 0 1.032 1.888ZM61.968 39.09l-.568-2.023a1.52 1.52 0 0 0-2.928.816l.568 2.024c.08.28.144.56.192.84a1.517 1.517 0 0 0 1.752 1.24 1.52 1.52 0 0 0 1.24-1.752 11.742 11.742 0 0 0-.256-1.144ZM42.968 44.979a5.959 5.959 0 0 1-.168-.536l-.656-2.36a1.52 1.52 0 0 0-2.928.816l.656 2.36c.072.248.144.496.232.736a1.52 1.52 0 0 0 1.944.92 1.502 1.502 0 0 0 .92-1.936ZM39.744 39.147a1.52 1.52 0 0 0 1.056-1.872l-.368-1.328a11.088 11.088 0 0 0-.712-1.88 1.522 1.522 0 0 0-2.736 1.328c.216.44.384.904.52 1.376l.368 1.328a1.52 1.52 0 0 0 1.464 1.112c.136-.008.272-.024.408-.064ZM54.152 52.963c.432-.12.864-.272 1.28-.44a1.519 1.519 0 1 0-1.16-2.808 7.589 7.589 0 0 1-.936.32l-.408.112c-.416.112-.84.2-1.264.248a1.515 1.515 0 0 0-1.328 1.688 1.514 1.514 0 0 0 1.504 1.344c.056 0 .12 0 .176-.008.576-.064 1.16-.184 1.72-.344l.416-.112ZM26.56 28.13c-1 .28-1.944.689-2.824 1.225a1.52 1.52 0 0 0-.512 2.088c.288.472.784.728 1.304.728.272 0 .544-.072.784-.224a8.318 8.318 0 0 1 2.432-.992c.816-.184 1.328-1 1.144-1.824a1.518 1.518 0 0 0-1.824-1.144c-.168.056-.336.096-.504.144ZM47.544 49.81a8.471 8.471 0 0 1-2.232-1.383 1.513 1.513 0 0 0-2.144.152 1.513 1.513 0 0 0 .152 2.144 11.252 11.252 0 0 0 3.048 1.896c.192.08.392.12.584.12.592 0 1.16-.352 1.4-.936a1.516 1.516 0 0 0-.808-1.992ZM60.808 43.979a1.517 1.517 0 0 0-1.944.912 8.173 8.173 0 0 1-1.272 2.296c-.52.664-.4 1.616.256 2.136a1.524 1.524 0 0 0 2.136-.264 11.295 11.295 0 0 0 1.736-3.144 1.524 1.524 0 0 0-.912-1.936ZM21.584 46.523a1.52 1.52 0 0 0 1.056-1.872l-.84-3.008a1.52 1.52 0 0 0-2.928.816l.84 3.008a1.52 1.52 0 0 0 1.872 1.056ZM19.736 38.619c.096.016.192.032.288.032.712 0 1.352-.504 1.488-1.232a8.227 8.227 0 0 1 .904-2.464 1.523 1.523 0 0 0-2.656-1.488 11.31 11.31 0 0 0-1.24 3.368 1.544 1.544 0 0 0 1.216 1.784ZM57.76 29.683a1.52 1.52 0 0 0-1.056 1.872l.416 1.488a1.527 1.527 0 0 0 1.872 1.056 1.52 1.52 0 0 0 1.056-1.872l-.416-1.488a1.52 1.52 0 0 0-1.872-1.056Z" fill="#4E2079" />
                      </svg>
                    </div>

                    <div className="d-flex flex-column gap-xs">
                      <div className="d-flex flex-lg-1 flex-row gap-s">
                        <div
                          className="bg-secondary text-bold text-white"
                          style={{ borderRadius: 20, padding: '0 10px' }}
                        >
                          Overall
                        </div>

                        {
                          overallSeries.target && (
                            <div className="align-items-center d-flex small text-bold gap-xxs">
                              <FontAwesomeIcon icon="bullseye" className="text-secondary" />
                              {' '}<span className="text-secondary">{overallSeries.target}</span>
                            </div>
                          )
                        }
                      </div>

                      <div style={{ lineHeight: 1.25 }}>
                        <span className="text-bold text-secondary" style={{ fontSize: 18 }}>
                          N/A -
                        </span>
                        {' '}
                        We'll show your estimated score and progress towards your target once you answer more practice questions.
                      </div>
                    </div>
                  </div>
                )
              )
            }

            <div className="d-flex flex-column flex-lg-row gap-m gap-lg-s">
              {
                without(props.data, overallSeries).map((series) => {
                  const colorPalette = {
                    dark: predeterminedColors(series.name, props.examName),
                    light: '#ffffff'
                  };

                  return (
                    <SeriesCard
                      {...series}
                      active={series.name === selectedSeries.name}
                      colorPalette={colorPalette}
                      onSelected={() => props.onChangeSelectedSeries(series)}
                    />
                  );
                })
              }
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 hidden-sm hidden-xs">
        <EstimatedScoreLineChart
          colorPalette={colorPaletteForLineChart()}
          series={selectedSeries}
        />
      </div>
    </div>
  );
};

export default EstimatedScore;
